/**
 * Prompt Compare Runner
 * A/B test two prompt versions side by side.
 */

import { useState } from 'react';
import apiClient from '../../lib/api';
import { toast } from 'sonner';

interface VersionOption {
  id: string;
  label: string;
}

interface PromptCompareRunnerProps {
  promptKey: string;
  variables: string[];
  versionOptions: VersionOption[];
}

export default function PromptCompareRunner({
  promptKey,
  variables,
  versionOptions,
}: PromptCompareRunnerProps) {
  const [versionA, setVersionA] = useState(versionOptions[0]?.id || 'default');
  const [versionB, setVersionB] = useState(versionOptions[1]?.id || versionOptions[0]?.id || 'default');
  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    Object.fromEntries(variables.map((v) => [v, '']))
  );
  const [result, setResult] = useState<{
    a: { label: string; resolvedPrompt: string; tokenEstimate: number };
    b: { label: string; resolvedPrompt: string; tokenEstimate: number };
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCompare = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post(
        `/admin/prompts/${encodeURIComponent(promptKey)}/compare`,
        { versionA, versionB, variables: variableValues }
      );
      setResult(response.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Vergelijking mislukt');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-bold text-black dark:text-white">A/B Vergelijking</h3>

      {/* Version selectors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">Versie A</label>
          <select
            value={versionA}
            onChange={(e) => setVersionA(e.target.value)}
            className="w-full px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white"
          >
            {versionOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-600 dark:text-gray-400 mb-1">Versie B</label>
          <select
            value={versionB}
            onChange={(e) => setVersionB(e.target.value)}
            className="w-full px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white"
          >
            {versionOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Variable inputs */}
      {variables.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {variables.map((v) => (
            <div key={v}>
              <label className="block text-xs font-mono text-gray-600 dark:text-gray-400 mb-1">
                {`{{${v}}}`}
              </label>
              <input
                type="text"
                value={variableValues[v] || ''}
                onChange={(e) =>
                  setVariableValues((prev) => ({ ...prev, [v]: e.target.value }))
                }
                className="w-full px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white font-mono"
              />
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleCompare}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-bold border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
      >
        {isLoading ? 'Vergelijken...' : 'Vergelijk'}
      </button>

      {/* Results side by side */}
      {result && (
        <div className="grid grid-cols-2 gap-4">
          {[result.a, result.b].map((side, i) => (
            <div key={i} className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span className="font-bold">{side.label}</span>
                <span className="font-mono">~{side.tokenEstimate} tokens</span>
              </div>
              <pre className="p-3 border-2 border-black dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs font-mono text-black dark:text-white whitespace-pre-wrap overflow-auto max-h-72">
                {side.resolvedPrompt}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
